// src/components/admin/AdminStatsWidget.js
import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const AdminStatsWidget = ({ data }) => {
  if (!data) return null;

  const statCards = [
    {
      title: 'Total Usuarios',
      value: data.totalUsers?.toLocaleString() || '0',
      icon: <PeopleIcon />,
      color: 'primary',
      trend: '+12%',
      subtitle: `${data.activeUsers} activos`
    },
    {
      title: 'Total Eventos',
      value: data.totalEvents?.toString() || '0',
      icon: <EventIcon />,
      color: 'success',
      trend: '+8%',
      subtitle: `${data.upcomingEvents} próximos`
    },
    {
      title: 'Inscripciones',
      value: data.totalRegistrations?.toLocaleString() || '0',
      icon: <AssignmentIcon />,
      color: 'info',
      trend: '+23%',
      subtitle: 'Este mes'
    },
    {
      title: 'Ingresos Totales',
      value: `$${(data.totalRevenue / 1000).toFixed(1)}K` || '$0',
      icon: <MoneyIcon />,
      color: 'warning',
      trend: '+18%',
      subtitle: 'En pesos mexicanos'
    }
  ];

  const roleStats = [
    {
      role: 'Atletas',
      count: data.athletesCount,
      percentage: (data.athletesCount / data.totalUsers * 100).toFixed(1),
      color: 'success',
      icon: '🏃‍♀️'
    },
    {
      role: 'Organizadores',
      count: data.organizersCount,
      percentage: (data.organizersCount / data.totalUsers * 100).toFixed(1),
      color: 'primary',
      icon: '📋'
    },
    {
      role: 'Jueces',
      count: data.judgesCount,
      percentage: (data.judgesCount / data.totalUsers * 100).toFixed(1),
      color: 'warning',
      icon: '⚖️'
    },
    {
      role: 'Fotógrafos',
      count: data.photographersCount,
      percentage: (data.photographersCount / data.totalUsers * 100).toFixed(1),
      color: 'info',
      icon: '📸'
    }
  ];

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📊 Estadísticas Generales"
        subheader="Resumen ejecutivo de la plataforma"
        action={
          <Chip 
            label="En vivo" 
            color="success" 
            size="small"
            sx={{ animation: 'pulse 2s infinite' }}
          />
        }
      />
      <CardContent>
        {/* Estadísticas principales */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: `${stat.color}.main`,
                    mx: 'auto',
                    mb: 1,
                    width: 40,
                    height: 40
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Typography variant="h5" fontWeight="bold" color={`${stat.color}.main`}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.title}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <Chip
                    label={stat.trend}
                    color={stat.trend.includes('+') ? 'success' : 'error'}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stat.subtitle}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Distribución por roles */}
        <Typography variant="h6" gutterBottom>
          👥 Distribución de Usuarios por Rol
        </Typography>
        
        <Grid container spacing={2}>
          {roleStats.map((role, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography fontSize="1.2rem">{role.icon}</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {role.role}
                  </Typography>
                </Box>
                
                <Typography variant="h5" color={`${role.color}.main`} fontWeight="bold">
                  {role.count}
                </Typography>
                
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(role.percentage)}
                    color={role.color}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {role.percentage}% del total
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Métricas adicionales */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {data.completedEvents}
                </Typography>
                <Typography variant="caption">Eventos Completados</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary.main">
                  {data.upcomingEvents}
                </Typography>
                <Typography variant="caption">Eventos Próximos</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="info.main">
                  {((data.activeUsers / data.totalUsers) * 100).toFixed(1)}%
                </Typography>
                <Typography variant="caption">Usuarios Activos</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdminStatsWidget;